import {
  Directive,
  Input,
  OnInit,
  OnDestroy,
  TemplateRef,
  ViewContainerRef
} from "@angular/core";
import { Subject, BehaviorSubject, combineLatest } from "rxjs";
import { map, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { LaunchDarklyService } from "./launchdarkly.service";

@Directive({ selector: "[ldIf]" })
export class LdIfDirective implements OnInit, OnDestroy {
  private flagKey$ = new BehaviorSubject<string | null>(null);
  private negate$ = new BehaviorSubject<boolean>(false);

  // *ldIf="'flagKey'; else otherTpl"
  @Input("ldIfElse") elseTpl?: TemplateRef<unknown>;

  // *ldIf="'flagKey'"
  @Input() set ldIf(flagKey: string) {
    this.flagKey$.next(flagKey);
  }

  // *ldIf="'flagKey'; negate: true"
  @Input() set ldIfNegate(val: boolean | string) {
    this.negate$.next(this.toBool(val));
  }

  private destroy$ = new Subject<void>();

  constructor(
    private ld: LaunchDarklyService,
    private tpl: TemplateRef<unknown>,
    private vc: ViewContainerRef
  ) {}

  ngOnInit(): void {
    combineLatest([this.ld.flags$, this.flagKey$, this.negate$])
      .pipe(
        map(([flags, key, negate]) => {
          const enabled = key ? !!flags[key] : false;
          return negate ? !enabled : enabled;
        }),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((show) => {
        this.vc.clear();
        if (show) {
          this.vc.createEmbeddedView(this.tpl);
        } else if (this.elseTpl) {
          this.vc.createEmbeddedView(this.elseTpl);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private toBool(v: boolean | string): boolean {
    if (typeof v === "boolean") return v;
    // Treat truthy strings like "true", "", and presence-without-value as true
    return ["", "true", "1", "yes", "on"].includes(
      String(v).trim().toLowerCase()
    );
  }
}

/********************************************
 *
 *
 * <!-- Show this block only if the "accreditations" flag is true -->
 *   <section *ldIf="'accreditations'">
 *     <h2>Accreditations</h2>
 *     <button>Create New</button>
 *   </section>
 *
 *    <!-- Another example -->
 *    <nav>
 *      <a routerLink="/dashboard">Dashboard</a>
 *      <a *ldIf="'betaFeatureX'" routerLink="/beta">Beta Feature X</a>
 *    </nav>
 *
 * <!-- With else template -->
 *    <ng-template #offState>
 *      <div>Fallback content when flag is OFF</div>
 *    </ng-template>
 *
 *    <div *ldIf="'accreditations'; else offState">
 *      Feature is enabled!
 *    </div>
 *
 * <!-- Negate the flag value -->
 *    <div *ldIf="'is-rosterezy'; negate: true">
 *      This shows when Rosterezy is OFF
 *   </div>
 ********************************************/
